
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Loader2, Search, SatelliteDish, Package, User } from "lucide-react";
import { useCreateServiceLine } from "@/domains/service-lines/services/account-service-lines";
import { useEffect, useMemo, useState } from "react";
import { useAccountProducts } from '@/domains/products/services/account-products';
import { useInfiniteAccountTerminals } from '@/domains/terminals/services/account-user-terminals';
import { UserTerminal } from '@/shared/types/userTerminalTypes';
import { ERROR, SUCCESS } from "@/config/toast-message-config";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { showError, showErrorWithApiError, showSuccess } from "@/shared/utils/toast-utils";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/shared/ui/radio-group";
import EmptyView from "@/shared/components/EmptyView";
import { CreateServiceLineRequest, ServiceLineType } from "@/shared/types/serviceLineTypes";
import { Organization } from "@/shared/types/userTypes";
import { DEBOUNCE_TIME } from "@/shared/utils/input-validator";
import SearchBar from "@/shared/components/SearchBar";
import { getTerminalTypeTextColorClass, TERMINAL_TYPE_INFO } from "@/shared/utils/color-utils-terminals";
import { TerminalType } from "@/shared/types/userTerminalTypes";
import { useAccountCustomers } from "@/domains/customers/services/account-customers";

interface CreateServiceLineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  organization: Organization;
}

const CreateServiceLineDialog = ({
  open,
  onOpenChange,
  onSuccess,
  organization,
}: CreateServiceLineDialogProps) => {
  const createServiceLine = useCreateServiceLine();
  const { data: customers, isLoading: isLoadingCustomers } = useAccountCustomers(open && organization.isKYCRequiredForServiceLines === true);

  const availableCustomers = useMemo(() => {
    return customers?.filter((customer) => customer.kycVerified === true);
  }, [customers]);

  // Fetch products data
  //TODO: Implement infinite scroll for products
  const { data: productsData, isLoading: isLoadingProducts } = useAccountProducts(open, 0);

  const sortedProducts = useMemo(() => {
    return productsData?.results?.sort((a, b) => (b?.dataProduct?.dataAmount || 0) - (a?.dataProduct?.dataAmount || 0));
  }, [productsData?.results]);

  const [formData, setFormData] = useState<CreateServiceLineRequest | null>(null);

  // Terminal states
  const [searchTerminalInput, setSearchTerminalInput] = useState<string>('');
  const [terminalSearchTerm, setTerminalSearchTerm] = useState("");

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setTerminalSearchTerm(searchTerminalInput.trim());
    }, DEBOUNCE_TIME);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerminalInput]);

  const {
    data: userTerminalsInfiniteData,
    isLoading: isLoadingUserTerminals,
    isFetching: isFetchingUserTerminals,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteAccountTerminals(open, false, terminalSearchTerm);

  const mergedUserTerminals = useMemo(() => {
    const pages = userTerminalsInfiniteData?.pages ?? [];
    const seen = new Set<string>();
    const merged: UserTerminal[] = [];
    for (const page of pages) {
      for (const t of page.results ?? []) {
        if (!seen.has(t.userTerminalId)) {
          seen.add(t.userTerminalId);
          merged.push(t);
        }
      }
    }
    return merged;
  }, [userTerminalsInfiniteData?.pages]);

  const totalTerminalCount = userTerminalsInfiniteData?.pages[0]?.totalCount ?? 0;

  // Get available terminals (no service line assigned)
  const availableTerminals = useMemo(() => {
    return mergedUserTerminals.filter((terminal) => !terminal.serviceLine?.serviceLineNumber);
  }, [mergedUserTerminals]);

  // Filter terminals based on search term
  const filteredTerminals = useMemo(() => {
    const searchTermLower = terminalSearchTerm.toLowerCase();
    return availableTerminals.filter(terminal =>
      terminal.userTerminalId.toLowerCase().includes(searchTermLower) ||
      terminal.kitSerialNumber.toLowerCase().includes(searchTermLower) ||
      terminal.dishSerialNumber.toLowerCase().includes(searchTermLower)
    );
  }, [availableTerminals, terminalSearchTerm]);

  const isSearchingTerminals =
    (searchTerminalInput.trim() !== terminalSearchTerm) ||
    (Boolean(terminalSearchTerm) && isFetchingUserTerminals);

  useEffect(() => {
    if (!open) {
      setFormData(null);
      setSearchTerminalInput('');
      setTerminalSearchTerm('');
    }
  }, [open]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData?.orbiusProductReferenceId) {
      showError(ERROR.SUBSCRIPTION.SELECT_STARLINK_SUBSCRIPTION);
      return;
    }

    if (!formData?.type) {
      showError(ERROR.SERVICE_LINE.SELECT_TYPE);
      return;
    }

    if (!formData?.serviceLineName) {
      showError(ERROR.SERVICE_LINE.ENTER_SERVICE_LINE_NAME);
      return;
    }

    if (formData?.terminalId) {
      const terminal = availableTerminals.find((t) => t.userTerminalId === formData?.terminalId);
      const product = sortedProducts.find((p) => p.orbiusProductReferenceId === formData?.orbiusProductReferenceId);
      if (terminal?.type === TerminalType.STARLINK_MINI && product?.l2vpnSupported === true) {
        showError(ERROR.SERVICE_LINE.STARLINK_MINI_L2VPN_UNSUPPORTED);
        return;
      }
    }

    try {
      // Step 1: Create service line
      await createServiceLine.mutateAsync({
        orbiusProductReferenceId: formData.orbiusProductReferenceId,
        type: formData.type,
        serviceLineName: formData.serviceLineName,
        customerId: formData.customerId ?? undefined,
        terminalId: formData.terminalId ?? undefined,
      });

      showSuccess(SUCCESS.SERVICE_LINE.CREATED);
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.SERVICE_LINE.CREATE);
    }
  };

  const isFormValid = useMemo(() => {
    return Boolean(
      formData?.orbiusProductReferenceId &&
      formData?.type &&
      formData?.serviceLineName &&
      (organization.isKYCRequiredForServiceLines === true ? formData?.customerId !== null : true)
    );
  }, [formData, organization.isKYCRequiredForServiceLines]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent id="create-service-line-dialog" onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Service Line</DialogTitle>
          <DialogDescription>Create a new service line for a subscription.</DialogDescription>
        </DialogHeader>
        {isLoadingProducts ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center gap-2">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span>Loading products...</span>
            </div>
          </div>
        ) : !sortedProducts || sortedProducts.length === 0 ? (
          <EmptyView
            icon={Package}
            size="small"
            title="No Subscriptions Available"
            description="This account doesn't have any subscriptions. You need to add a subscription before creating a service line."
          />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              {/* Service Line Name */}
              <div className="space-y-2">
                <Label htmlFor="service-line-name">Service Line Name</Label>
                <Input
                  id="service-line-name"
                  value={formData?.serviceLineName || ''}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      serviceLineName: e.target.value,
                    }));
                  }}
                  required={true}
                  placeholder="Enter service line nickname..."
                  className="w-full"
                />
              </div>

              {/* Service Line Type */}
              <div className="space-y-2">
                <Label htmlFor="service-line-type">Service Line Type</Label>
                <Select
                  value={formData?.type ?? undefined}
                  onValueChange={(value) =>
                    setFormData(prev => ({
                      ...prev,
                      type: value as ServiceLineType,
                    }))
                  }
                >
                  <SelectTrigger id="service-line-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(ServiceLineType).map((option) => {
                      return (
                        <SelectItem
                          key={option}
                          value={option}
                        >
                          {option}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Subscription Selection */}
              <div className="space-y-2">
                <p className="text-sm font-medium">Subscription</p>
                {isLoadingProducts ? (
                  <div className="flex items-center gap-2 p-3 border rounded-md">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Loading subscriptions...</span>
                  </div>
                ) : sortedProducts && sortedProducts.length > 0 ? (
                  <Select
                    value={formData?.orbiusProductReferenceId || ''}
                    onValueChange={(id) => {
                      setFormData(prev => ({
                        ...prev,
                        orbiusProductReferenceId: id,
                      }));
                    }}
                    required={true}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Subscription" />
                    </SelectTrigger>
                    <SelectContent>
                      {sortedProducts.map((s) => (
                        <SelectItem key={s.orbiusProductReferenceId} value={s.orbiusProductReferenceId}>
                          {s.orbiusProductName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="p-3 border rounded-md bg-muted/50">
                    <p className="text-sm">No subscriptions available for this account.</p>
                  </div>
                )}
              </div>

              {/* Customer Selection */}
              {organization.isKYCRequiredForServiceLines === true && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Customer</p>
                  {availableCustomers?.length > 0 ? (
                    <Select
                      value={formData?.customerId || ''}
                      onValueChange={(value) => {
                        setFormData(prev => ({
                          ...prev,
                          customerId: value,
                        }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCustomers?.map((customer) => (
                          <SelectItem key={customer.customerId} value={customer.customerId}>
                            {customer.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <EmptyView
                      icon={User}
                      size="small"
                      title="No Customers Available"
                      description="There are no customers for this account."
                    />
                  )}
                </div>
              )}

              {/* Terminal Selection */}
              <div className="space-y-2 flex flex-col gap-2">
                <p className="text-sm font-medium">Available Terminal(s) (Optional)</p>
                <>
                  <div className="relative">
                    <SearchBar value={searchTerminalInput} onChange={setSearchTerminalInput} placeholder="Search for a terminal..." />
                  </div>
                  {isSearchingTerminals && (
                    <div className="flex items-center gap-2 text-xs">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Searching terminals...</span>
                    </div>
                  )}

                  {isLoadingUserTerminals && mergedUserTerminals.length === 0 ? (
                    <div className="flex items-center justify-center gap-2 min-h-[200px]">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Loading terminals…</span>
                    </div>
                  ) : filteredTerminals.length > 0 ? (
                    <ScrollArea className="max-h-[300px] w-full rounded-md border">
                      <div className="p-4 space-y-2">
                        <RadioGroup
                          value={formData?.terminalId || ''}
                          onValueChange={(value) => {
                            setFormData((prev) => ({
                              ...prev,
                              terminalId: value,
                            }));
                          }}
                          className="space-y-2"
                        >
                          {filteredTerminals.map((terminal) => (
                            <div key={terminal.userTerminalId} className="flex items-center space-x-2">
                              <RadioGroupItem
                                value={terminal.userTerminalId}
                                id={`terminal-${terminal.userTerminalId}`}
                              />
                              <Label
                                htmlFor={`terminal-${terminal.userTerminalId}`}
                                className="text-sm font-normal flex-1"
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{terminal.userTerminalId}</span>
                                  <span className="text-xs">
                                    Kit: {terminal.kitSerialNumber} | Serial: {terminal.dishSerialNumber}
                                  </span>
                                  {terminal.type && (
                                    <span className={`text-xs ${getTerminalTypeTextColorClass(terminal.type)}`}>{TERMINAL_TYPE_INFO[terminal.type].value}</span>
                                  )}
                                </div>
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </div>
                    </ScrollArea>
                  ) : terminalSearchTerm ? (
                    <EmptyView
                      icon={Search}
                      size="small"
                      title="No Terminals Found"
                      description={`No terminals match "${terminalSearchTerm}". Try adjusting your search.`}
                    />
                  ) : hasNextPage ? (
                    <EmptyView
                      icon={SatelliteDish}
                      size="small"
                      title="No Unassigned Terminals Yet"
                      description="No unassigned terminals on the pages loaded so far. Load more to search further."
                    />
                  ) : mergedUserTerminals.length > 0 ? (
                    <EmptyView
                      icon={SatelliteDish}
                      size="small"
                      title="No Unassigned Terminals"
                      description="All terminals loaded for this account are already assigned to a service line."
                    />
                  ) : (
                    <EmptyView
                      icon={SatelliteDish}
                      size="small"
                      title="No Terminals Available"
                      description="There are no terminals for this account."
                    />
                  )}
                  {(hasNextPage || (totalTerminalCount > 0 && mergedUserTerminals.length > 0)) && (
                    <div className="glass-panel flex flex-col gap-2 border-t p-2 rounded-lg">
                      {totalTerminalCount > 0 && (
                        <>
                          <p className="text-xs">
                            Select a terminal that has no service line assigned to automatically assign it to this new service line.
                          </p>
                          <p className="text-xs">
                            Loaded {mergedUserTerminals.length} of {totalTerminalCount} terminals
                            {availableTerminals.length < mergedUserTerminals.length && (
                              <span> ({availableTerminals.length} unassigned)</span>
                            )}
                          </p>
                        </>
                      )}
                      {hasNextPage ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full gap-2"
                          disabled={isFetchingNextPage}
                          onClick={() => void fetchNextPage()}
                        >
                          {isFetchingNextPage ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Loading…
                            </>
                          ) : (
                            'Load more'
                          )}
                        </Button>
                      ) : null}
                    </div>
                  )}
                </>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={createServiceLine.isPending}
                className="gap-2"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createServiceLine.isPending || isLoadingProducts || !productsData || productsData.results.length === 0 || !isFormValid}
                className="gap-2">
                {createServiceLine.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Service Line'
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CreateServiceLineDialog; 